import { CloudStream, CloudStreamConnectionState, StreamConnectionState, useCloudStream, useCloudStreamOptions, useCloudStreamState, useZLBolt, useZLBoltOptions, useZLBoltStreamState, ZLBolt } from "@zerolight-core/libzl-react";
import LibZL, { AppStateData, Camera2DOD, CloudStreamOptions, ZLBoltMode, ZLBoltOptions } from "@zerolight-core/libzl";
import { ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { VisualizerSlice } from "../store/reducers/visualizer";
import styled from "styled-components";
import { GenerationOptions } from "@zerolight-core/libzl/types/src/modules/direct2d/direct2d";
import { ConfigObject, FrameConfig, FrameConfigType } from "@zerolight-core/zlthunder-2dod";
//import { trackingData } from "../CloudMine";
import { useIdle, useSearchParam } from "react-use";
import { JSONEquality } from "../utils/equal";
import { CloudStreamVisualizerState, ZLVEVisualizerState } from "../types/VisualizerTypes";
import { requireToken, useToken } from "../utils/auth";
import { useMediaQueries } from "@react-hook/media-query";
import { disconnectDelay } from "../config";
import { getInitialUrlArgs, isDebugFeature } from "../UrlArgsProvider";
import { useBuildSession } from "../BuildSession";

const { debug } = getInitialUrlArgs();
const verbose = debug || isDebugFeature('Visualizer');

const Wrapper = styled.div<{"data-active": boolean}>`
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0;
    opacity: 1;
    transition: opacity 0.5s ease-in-out;

    &[data-active=false]{
        visibility: none;
        pointer-events: none;
        opacity: 0;
    }
`

const Style = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`

export function Is3DAllowed(modes: ZLBoltMode[], configurationChanges: number){
    const {force2D, force3D, check3DUpgrade, check3DUpgradeRegion, min3DTimeZone, max3DTimeZone, lang3D, check3DUpgradeInteraction, min3DConfigChanges} = getInitialUrlArgs()

    if(force2D != force3D) {
        if(force2D) {
            modes = modes.filter(mode => mode === "2D")
        }
        if(force3D) {
            modes = modes.filter(mode => mode === "3D")
        }
    }

    if (!force2D && !force3D && check3DUpgrade)
    {
        if(check3DUpgradeRegion) {
            const offset = new Date().getTimezoneOffset() / 60;
            const allow3DUpgradeBasedOnRegion = min3DTimeZone <= offset && offset <= max3DTimeZone && lang3D.split(",").includes(navigator.language);
            if (!allow3DUpgradeBasedOnRegion)
            {
                //console.warn("3D Upgrade not available", offset, navigator.language, min3DTimeZone, max3DTimeZone, lang3D);
                return false;
            }
        }

        if(check3DUpgradeInteraction) {
            if(configurationChanges < min3DConfigChanges) {
                console.debug("3D Upgrade not available yet", {current: configurationChanges, required: min3DConfigChanges});
                return false;
            }
        }
    }

    return modes.includes("3D");
}

export function
    Visualizer(props: { children: ReactNode, modes: ZLBoltMode[], modeSpecificChildren?: Record<ZLBoltMode, ReactNode> })
{
    const mode = useAppSelector(s => s.configuration.currentMode);
    const zlboltOptions = useAppSelector(s => "zlboltOptions" in s.configuration.currentVisualizerConfig);
    const cloudstreamOptions = useAppSelector(s => "cloudStreamOptions" in s.configuration.currentVisualizerConfig);

    const UnitComponent = zlboltOptions ? VisualizerUnitZLVE : cloudstreamOptions ? VisualizerUnitCloudStream : undefined;

    const {modes} = props

    if (!UnitComponent)
    {
        return <>No Visualizer configuration provided</>
    }
    return <Style>
        {modes.map((m, key) =>
            <Wrapper key={key} data-active={mode == m} data-mode={m} data-testid={`wrapper-${m}`} >
                <UnitComponent mode={m}>
                    {props.modeSpecificChildren?.[m]}
                    {props.children}
                </UnitComponent>
            </Wrapper>
        )}
    </Style>
}

let streamId = 0;

export function VisualizerUnitZLVE(props: { mode: ZLBoltMode, children: ReactNode })
{
    const {assetService, localStream, starterPack, shortcode, configId} = getInitialUrlArgs();
    const {initialConfigData} = useBuildSession()
    const region = useSearchParam("region");

    const zlboltOptions = useAppSelector(s => "zlboltOptions" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.zlboltOptions : undefined as never);
    const camera2DConfig = useAppSelector(s => "camera2DConfig" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.camera2DConfig : undefined as never);

    //Parent id should be generated only once during component lifetime
    const parentId = useMemo(() => `stream-container-${props.mode}-${streamId++}`, []); 

    const token = useToken();
    const needToken = requireToken(assetService)
    const { environment, camera, prcodes, animationStates } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, () => true);;
    const remapCamera = useCameraRemap();
    const cameraMemo = useMemo(() => remapCamera(camera), []);

    const buildSessionLoaded = useAppSelector((s) => s.configuration.ready);

    const setupPrCodes = buildSessionLoaded ? prcodes : initialConfigData?.prcodes ?? undefined;
    const zlbolt = useMemo(() => UpdateLibZLInstance(new LibZL()).zlbolt(), []);
    const newOptions:ZLBoltOptions  = {
        ...zlboltOptions,

        connectionParameters: {
            ...zlboltOptions.connectionParameters!,
            ...assetService ? { environment: assetService } : {},
            ...region ? { region: region } : {},
        },

        recordSessionDetails: true,
        setupEnvironment: environment,
        setupCamera: cameraMemo,
        setupPrCodes,
        setupAnimStates: animationStates,
        modes: [props.mode],

        consumeTouchDownUpEvents: props.mode === "3D",
        consumeTouchMoveEvents: props.mode === "3D",
        consumeMouseClickEvents: props.mode === "3D",
        consumeMouseScrollEvents: props.mode === "3D",

        parent: parentId,
        ...token,
    }

    if(props.mode === "3D") {
        if (localStream) {
            let host = localStream
            let protocol = window.location.protocol.replace(":", "")
            try {
                const url = new URL(localStream)
                host = url.host
                protocol = url.protocol.replace(":", "")
            }
            catch {
                console.debug(`localStream parameter provided '${localStream}' is not a fully qualified origin, assuming the same protocol as the webpage`)
            }
      
            newOptions.directConnect =  `${host}/zlm/sub/0/ve`
            newOptions.allow2DFallback = false
            newOptions.overrideMetadataEndpoint = `${protocol}://${host}/zlm/localmetadataservice/`
            newOptions.localControl = true
            newOptions.idleDowngrade = -1
            newOptions.allow2DFallback = false
        }
    }

    zlbolt.then(instance => window[("zlbolt" + props.mode) as never] = instance as never);

    const newOptionsStr = JSON.stringify(newOptions)
    const options = useMemo(()  => JSON.parse(newOptionsStr), [newOptionsStr]);
    
    if (!setupPrCodes || !buildSessionLoaded)
        return null;

    if (needToken && !token)
        return null;

    if((shortcode || configId || starterPack) && !initialConfigData) {
        return null;
    }

    zlbolt.then(r => r.ondemand2dCamData = camera2DConfig as Record<string, Camera2DOD>);

    return <ZLBolt options={options} connect zlbolt={zlbolt}>
        <VisualizerZLVEDriver mode={props.mode}>
            {props.children}
        </VisualizerZLVEDriver>
    </ZLBolt>
}

const CloudStreamModesMap = { "2D": "image", "3D": "video"} as const;
const ZLBoltModesMap = { "image": "2D", "video": "3D" } as const;

function UpdateLibZLInstance(libzl: LibZL)
{
    //libzl.identifierData.instanceId = trackingData.ExecutionContextID;
    return libzl;
}

export function VisualizerUnitCloudStream(props: { mode: ZLBoltMode, children: ReactNode  })
{
    const {assetService} = getInitialUrlArgs()
    const region = useSearchParam("region");

    const cloudStreamOptions = useAppSelector(s => "cloudStreamOptions" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.cloudStreamOptions! : undefined as never);

    const parentclass = useMemo(() => `stream-container-${props.mode}`, [props.mode]);
    const token = useToken();
    const needToken = requireToken(assetService)
    const initialAppStateData = useAppSelector((s) => VisualizerSlice.getSelectors().initialVisualiserState(s.configuration), JSONEquality);
    const cloudStream = useMemo(() => UpdateLibZLInstance(new LibZL()).cloudstream(), []);

    useEffect(() =>
        console.log("Initial CloudStream state", initialAppStateData)
    , [initialAppStateData])
    
    const newOptions:CloudStreamOptions  = {
        ...cloudStreamOptions,
        cloudConnectionParameters: {
            ...cloudStreamOptions.cloudConnectionParameters,
            ...assetService ? { renderService: assetService } : {},
            ...region ? { region: region } : {},
        },
        //recordSessionDetails: true, //TODO [GO3D-53]: This not yet supported in LibZL cloud stream options. 
        streamingMode: CloudStreamModesMap[props.mode],
        initialAppStateData: initialAppStateData as AppStateData,
        parent: parentclass,
        ...token,
    }

    cloudStream.then(instance => window[("cloudStream" + props.mode) as never] = instance as never);

    const newOptionsStr = JSON.stringify(newOptions)
    const options = useMemo(()  => JSON.parse(newOptionsStr), [newOptionsStr]);
    console.log(options);
    
    if (needToken && !token)
        return <></>;

    //zlbolt.then(r => r.ondemand2dCamData = camera2DConfig as any);
    return <CloudStream options={options} connect cloudstream={cloudStream}>
        <VisualizerCloudStreamDriver>
            {props.children}
        </VisualizerCloudStreamDriver>
    </CloudStream>
}

export type VisualizerStateReducer = (state: ZLVEVisualizerState) => ZLVEVisualizerState;

export function VisualizerCloudStreamDriver(props: { children?: ReactNode })
{
    const dispatch = useAppDispatch();
    const state = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as CloudStreamVisualizerState, JSONEquality);
    const requestedMode = useAppSelector(s => s.configuration.requestedMode);
    const reconnectAllowed = useAppSelector(s => s.configuration.reconnectAllowed);
    console.log("Cloudstream state", state);
    const cloudStream = useCloudStream();
    const streamState = useCloudStreamState();
    const isReady = streamState == CloudStreamConnectionState.Ready;
    const mode = cloudStream?.getStreamingMode() as "image" | "video";
    const modeState = useAppSelector(s => cloudStream && s.configuration.modesReady[ZLBoltModesMap[mode]]);
    const options = useCloudStreamOptions();

    useEffect(() =>
    {
        const handleFrameRequest = (data: unknown, frame: unknown) =>
        {
            console.log("2DFrameRequest", data, frame)
        }

        const fetchFramesRequest = (frameConfigs: ConfigObject[], generationOptions: GenerationOptions, context: string) =>
        {
                console.log("fetchFramesRequest", frameConfigs, generationOptions, context)
        }
        const makeFrameConfig = (frameConfig: FrameConfig, baseConfig: FrameConfigType, context: string) =>
        {
            console.log("makeFrameConfig", frameConfig, baseConfig, context);
        }
        cloudStream?.addEventListener("2DFrameRequest", handleFrameRequest);
        cloudStream?.addEventListener("fetchFramesRequest", fetchFramesRequest);
        cloudStream?.addEventListener("makeFrameConfig", makeFrameConfig);

        return () =>
        {
            cloudStream?.removeEventListener("2DFrameRequest", handleFrameRequest)
            cloudStream?.addEventListener("fetchFramesRequest", fetchFramesRequest);
            cloudStream?.addEventListener("makeFrameConfig", makeFrameConfig);
        };
    },[cloudStream])

    useEffect(() =>
    {
        console.log("RRR reconnect", mode, requestedMode, streamState, modeState, reconnectAllowed)

        if ((reconnectAllowed || ZLBoltModesMap[mode] != "3D") &&  !modeState && requestedMode == ZLBoltModesMap[mode] && (streamState == CloudStreamConnectionState.Disconnected || streamState == CloudStreamConnectionState.ConnectionError))
        {
            console.log("Doing reconnect", mode, requestedMode, streamState, modeState, reconnectAllowed)
            cloudStream.connect(options);
        }
    }, [requestedMode, mode, streamState, cloudStream, modeState, reconnectAllowed])
    
    useEffect(() =>
    {
        console.log(cloudStream, mode, modeState, isReady);
        dispatch(VisualizerSlice.actions.setModeState({ mode: ZLBoltModesMap[mode], ready: isReady, streamState: streamState as never as StreamConnectionState }));
    },[cloudStream, mode, modeState, isReady, streamState, dispatch])

    useEffect(() =>
    {
        console.log(cloudStream, mode, modeState, isReady);
        dispatch(VisualizerSlice.actions.setModeState({ mode: ZLBoltModesMap[mode], ready: isReady, streamState: streamState as never as StreamConnectionState }));
    }, [cloudStream, mode, modeState, isReady, streamState, dispatch])
    
    useEffect(() =>
    {
        return () => { dispatch(VisualizerSlice.actions.visualizerClosed()) };
    }, [dispatch]);

    useEffect(() =>
    {
        if (isReady) cloudStream.setAppStateData({ ...state });
    },[cloudStream, isReady, state])

    return <>
        {props.children}
    </>
}

function useCameraRemap()
{
    const cameraRemap = useAppSelector(s => "mediaCameraRemap" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.mediaCameraRemap : {}, JSONEquality);
    const query = Object.fromEntries(Object.keys(cameraRemap ?? []).map(query => [query, query]));

    const mediaQueries = useMediaQueries(query);

    return useCallback((camera: string) =>
    {
        if (!cameraRemap)
            return camera;

        for (const query in mediaQueries.matches)
        {
            if (!mediaQueries.matches[query])
                continue;
            console.log("[Camera Remap] Matched query", query);
            camera = cameraRemap[query][camera];
        }
        return camera;
    }, [cameraRemap, mediaQueries.matches]);
}

export function VisualizerZLVEDriver(props: { mode: ZLBoltMode, children?: ReactNode })
{
    const dispatch = useAppDispatch();
    const movableCamera = useAppSelector(s => "movableCamera" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.movableCamera : undefined, JSONEquality);
    const cameraSwitchOptions = useAppSelector(s => "cameraSwitchOptions" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.cameraSwitchOptions : undefined, JSONEquality);
    const { environment, camera, prcodes, preview, animationStates, instantAnimations, forceAnimationStates } = useAppSelector((s) => VisualizerSlice.getSelectors().visualiserState(s.configuration) as ZLVEVisualizerState, JSONEquality);
    const requestedMode = useAppSelector(s => s.configuration.requestedMode);
    const currentMode = useAppSelector(s => s.configuration.currentMode);
    const cameraRemap = useCameraRemap();
    const reconnectAllowed = useAppSelector(s => s.configuration.reconnectAllowed);

    const zlbolt = useZLBolt();
    const streamState = useZLBoltStreamState();
    const isReady = streamState == StreamConnectionState.Ready;
    const [pendingPrCodeStr, setPendingPrCodeStr] = useState(zlbolt?.getPrCodes() ?? "")
    const [activePrCodeStr, setActivePrCodeStr] = useState(zlbolt?.getPrCodes() ?? "")
    const mode = props.mode;
    const modeState = useAppSelector(s => zlbolt && s.configuration.modesReady[mode]);
    const options = useZLBoltOptions();
    const [cameraMoving, setCameraMoving] = useState(false);
    const [prcodesChanging, setPRcodesChanging] = useState(true);
    const [renderingImages, setRenderingImages] = useState(false);
    const [animationInProgress, setAnimationInProgress] = useState(false);
    const idleTimeoutInProgress = useAppSelector(s => !!s.configuration.idleTimeout);
    const {focusedStreamInteraction, allow2DCustomWraps, debug} = getInitialUrlArgs()
    const isIdle = useIdle(30000);
    const [overrideUnfocusedBlocker, setOverrideUnfocusedBlocker] = useState(false);
	const interactionTimer = useRef<NodeJS.Timeout>(null);
    const [forceReconnect, setForceReconnect] = useState(false)

    const onInteraction = (initiator: string) => {
		if (interactionTimer.current) {
			clearTimeout(interactionTimer.current);
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			verbose && console.log(`[Visualiser] additional interaction [${initiator}]`);
		} else {
			setOverrideUnfocusedBlocker(true);
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			verbose && console.log(`[Visualiser] setOverrideUnfocusedBlocker(true) [${initiator}]`);
		}
        interactionTimer.current = setTimeout(() => {
			interactionTimer.current = null;
			// eslint-disable-next-line @typescript-eslint/no-unused-expressions
			verbose && console.log('[Visualiser] setOverrideUnfocusedBlocker(false)');
            setOverrideUnfocusedBlocker(false);
        }, 10000)
	}

    const stateChangeAllowed = isReady && (requestedMode == props.mode || currentMode == props.mode || idleTimeoutInProgress || isIdle);

    useEffect(() =>
    {

        const onIdle = () => {
            dispatch(VisualizerSlice.actions.setIdleTimer(disconnectDelay));
        }
        const onActive = () => {
            dispatch(VisualizerSlice.actions.setIdleTimer(undefined));
        }
        const onCameraMove = () =>
        {
            zlbolt.clearStreamIdleTimeout();
            dispatch(VisualizerSlice.actions.setIdleTimer(undefined));
            onInteraction("onCameraMove")
        }
        const onCameraMoved = () => {
            onInteraction("onCameraMoved")
        }

        const handleFrameRequest = (data: unknown, frame: unknown) => {
            console.log("2DFrameRequest", data, frame)
        }
        const fetchFramesRequest = (frameConfigs: ConfigObject[], generationOptions: GenerationOptions, context: string) => {
            console.log("fetchFramesRequest", frameConfigs, generationOptions, context)
        }
        const makeFrameConfig = (frameConfig: FrameConfig, baseConfig: FrameConfigType, context: string) => {
            console.log("makeFrameConfig", frameConfig, baseConfig, context);
        }
        const handleGenerationError = (error: {errorCode: string}) => {
            try {
				if(error.errorCode === "TOKEN_ALREADY_CLAIMED") {
					console.log('Zlbolt had an error, reconnecting: ', error);
                    setForceReconnect(true);
					return
				}
				console.error('Zlbolt had an error', error);
			}
			catch (e) {
				console.error("Exception thrown during error CB", e);
			}
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const onProgressBarChanged = (_:unknown, event: { type: "value" | "setup" | "visibility"; segments?: any; totalMax?: any; id?: any; percent?: any; value?: any; state?: any; }) => 
        {
            if (event.type == 'visibility')
            {
                setRenderingImages(event.state);
                dispatch(VisualizerSlice.actions.setStreamBusy({mode, busy: event.state}))
            }
        }

        if (mode == "3D") {
            zlbolt?.addEventListener("streamBecameIdle", onIdle)
            zlbolt?.addEventListener("streamBecameActive", onActive)
            zlbolt?.addEventListener("cameraMoving", onCameraMove)
            zlbolt?.addEventListener("cameraMoved", onCameraMoved)
        } 
        else {
            if(debug) {
                zlbolt?.addEventListener("2DFrameRequest", handleFrameRequest);
                zlbolt?.addEventListener("fetchFramesRequest", fetchFramesRequest);
                zlbolt?.addEventListener("makeFrameConfig", makeFrameConfig);
            }
            zlbolt?.addEventListener('2DProgressBarChanged', onProgressBarChanged);

            zlbolt?.addEventListener('error', handleGenerationError);
        }

        return () =>
        { 
            if (mode == "3D") {
                zlbolt?.removeEventListener("streamBecameIdle", onIdle)
                zlbolt?.removeEventListener("streamBecameActive", onActive)
                zlbolt?.removeEventListener("cameraMoving", onCameraMove)
                zlbolt?.removeEventListener("cameraMoved", onCameraMoved)
            }
            else {
                if(debug) {
                    zlbolt?.removeEventListener("2DFrameRequest", handleFrameRequest)
                    zlbolt?.removeEventListener("fetchFramesRequest", fetchFramesRequest);
                    zlbolt?.removeEventListener("makeFrameConfig", makeFrameConfig);
                }
                zlbolt?.removeEventListener('2DProgressBarChanged', onProgressBarChanged);
                zlbolt?.removeEventListener('error', handleGenerationError);
            }
        };
    },[mode, zlbolt, dispatch, debug])

    const finalPrCodes = ((mode === "3D" || allow2DCustomWraps) && preview) ?
                            [...prcodes.filter(code => !/__d\(\d+:\d+:\d+\)__s\(\d+:\d+:\d+\)/i.test(code)), ...preview]
                            :
                            prcodes
    const prcodesStr = finalPrCodes.join(",") ?? ""

    useEffect(() =>
    {
        if (forceReconnect || (requestedMode == mode && (streamState == StreamConnectionState.Disconnected || streamState == StreamConnectionState.ConnectionError) && reconnectAllowed))
        {
            if(forceReconnect) {
                setForceReconnect(false)
            }
            zlbolt.disconnect();
            console.log("Doing reconnect", mode, requestedMode, streamState)
            zlbolt.connect({
                ...options,
                setupEnvironment: environment,
                setupCamera: camera,
                setupAnimStates: animationStates,
                setupPrCodes: prcodesStr,
            });
        }
    },[forceReconnect, requestedMode, mode, streamState, zlbolt, reconnectAllowed, environment, camera, animationStates, prcodesStr, options])
        
    useEffect(() =>
    {
        //console.log(zlbolt, mode, modeState, isReady);
        return () => { dispatch(VisualizerSlice.actions.visualizerClosed()) };
    }, [dispatch]);

    if(zlbolt)
        zlbolt.movableExteriorCamera = movableCamera ?? "GlobalStageCamera";

    useEffect(() => {
        if (!isReady || !stateChangeAllowed || animationInProgress)
            return;
        const states = zlbolt.getAnimStates();
        const targetStates = mode === "2D" ? forceAnimationStates : animationStates
        //console.log(states, animationStates);
        zlbolt.batchChanges(() =>
        {
            let allInstant = true;
            for (const key in targetStates) {
                if (!!states[key] != !!targetStates[key])
                {
                    //console.log("Animation state change", key, !!targetStates[key]);
                    zlbolt.setAnimation(key, !!targetStates[key], !!instantAnimations[key]);
                    allInstant &&= !!instantAnimations[key];
                    onInteraction("setAnimation")
                }
            }
        
            if (!allInstant && mode == "3D")
            {
                setAnimationInProgress(true);
                setTimeout(() => setAnimationInProgress(false), 2000);
            }
        })
    },[animationStates, forceAnimationStates, zlbolt, isReady, instantAnimations, mode, stateChangeAllowed, animationInProgress, setAnimationInProgress])

    useEffect(() =>
    {
        if (!stateChangeAllowed)
            return;

        if (!isReady || cameraMoving || animationInProgress)
            return 

        const currentCamera = zlbolt?.getCamera();

        const switchMode = cameraSwitchOptions?.find(rule => rule.cameras.includes(camera) && rule.cameras.includes(currentCamera))
        const remappedCamera = cameraRemap(camera);

        if (remappedCamera == currentCamera)
            return;

        if (remappedCamera != currentCamera && switchMode?.switchOptions.smooth && mode == "3D")
        {
            setCameraMoving(true);
            setTimeout(() => setCameraMoving(false), 2000);
        }

        zlbolt.switchCamera(remappedCamera as string, switchMode?.switchOptions);

        onInteraction("zlbolt.switchCamera")

    },[camera, zlbolt, isReady, cameraRemap, cameraSwitchOptions, mode, cameraMoving, stateChangeAllowed, animationInProgress])

    useEffect(() =>
    {
        if (!stateChangeAllowed)
            return;

        if (!isReady || cameraMoving) 
            return
        
		const actuallySetPrCodes = pendingPrCodeStr === activePrCodeStr && prcodesStr !== activePrCodeStr;
        if (actuallySetPrCodes)
        {
            setPendingPrCodeStr(prcodesStr)
            console.log(mode + ": Set PR Codes", prcodesStr)
            setPRcodesChanging(true);
            zlbolt.setPrCodes(prcodesStr, {}, () => { setActivePrCodeStr(prcodesStr) });
            setTimeout(() => setPRcodesChanging(false), 2000);
        }

        onInteraction(actuallySetPrCodes ? "zlbolt.setPrCodes" : 'zlbolt.setPrCodes (NOT)')
    }, [prcodesStr, zlbolt, isReady, pendingPrCodeStr, activePrCodeStr, cameraMoving, mode, stateChangeAllowed])

    useEffect(() =>
    {
        if (!stateChangeAllowed)
            return;

        const onChange = () => mode == "3D" && dispatch(VisualizerSlice.actions.setStreamBusy({ mode, busy: true }));
        const onChanged = () => {
            if(mode == "3D") {
                dispatch(VisualizerSlice.actions.setStreamBusy({ mode, busy: false }))
            }
            
        };

        if (isReady)
        {
            zlbolt.addEventListener("environmentChanged", onChanged);
            zlbolt.switchEnvironment(environment);
        }
        
        onChange();
        onInteraction(isReady ? "zlbolt.switchEnvironment" :  "zlbolt.switchEnvironment (NOT)")

        return () =>
        {
            if (isReady)
                zlbolt.removeEventListener("environmentChanged", onChanged)
        }
        
    }, [environment, zlbolt, isReady, dispatch, mode, stateChangeAllowed])

    const readyToShow = isReady && !prcodesChanging && !cameraMoving && zlbolt.getPrCodes() == prcodesStr && (mode == "3D" || !renderingImages) || isReady && modeState;
    useEffect(() =>
    {
        console.log("SetMode", mode, modeState, isReady, readyToShow);
        dispatch(VisualizerSlice.actions.setModeState({ mode: mode, ready: readyToShow, streamState: streamState }));
    },[zlbolt, readyToShow, mode, modeState, isReady, streamState, dispatch])

    const unfocusedInputBlockerId = useId()
    const focusStream = () => {
        const controller = zlbolt.getStreamController()
        controller?.streamOutput?.streamElement?.focus()
    }

    if(props.mode === "3D" && focusedStreamInteraction) {    
        return (
            <Container $unfocusedInputBlockerId={unfocusedInputBlockerId}>
                {props.children}
                {!overrideUnfocusedBlocker && <UnfocusedInputBlocker id={unfocusedInputBlockerId} onClick={focusStream}/>}
            </Container>
        )
    }

    return props.children
}

const Container = styled.div<{$unfocusedInputBlockerId: string}>`
    position: relative;
    width: 100%;
    height: 100%;

	& #${props => props.$unfocusedInputBlockerId} {
		/* background-color: magenta; opacity: 50% */
	}

	[data-active="true"] > &:focus-within > #${props => props.$unfocusedInputBlockerId} {
		/* background-color: yellow; */
    }

    [data-active="true"]>&:not(:focus-within)>#${props => props.$unfocusedInputBlockerId} {
        /* 
			// NOTE: [PD] - disabling this as it causes SLAT-511 & SLAT-509, - discussed with Helton
			// Alternative was to make it more obvious when we have the overlay AND the stream is unfocussed
			pointer-events: auto; 
		*/
		/* background-color: cyan; */
    }
`

const UnfocusedInputBlocker = styled.div`
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: 0;
`